import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, User, Package } from "lucide-react";
import { useCriarTransacaoFinanceira } from "@/hooks/useFinanceiro";
import { useClientes } from "@/hooks/useClientes";
import { useFornecedores } from "@/hooks/useFornecedores";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";
import { toast } from "sonner";

export type DadosIniciaisDespesa = {
  sinistroId?: number;
  clienteId?: number;
  valor?: number;
  observacoes?: string;
  dataVencimento?: string;
  titulo?: string;
};

interface NovaDespesaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  dadosIniciais?: DadosIniciaisDespesa | null;
}

/**
 * Dialog para cadastrar conta a pagar (despesa).
 * Permite selecionar Cliente ou Fornecedor como beneficiário.
 */
function resetForm(
  setters: {
    setBeneficiario: (v: string) => void;
    setValor: (v: string) => void;
    setDataVencimento: (v: string) => void;
    setFormaPagamento: (v: string) => void;
    setClienteId: (v: string) => void;
    setFornecedorId: (v: string) => void;
    setObservacoes: (v: string) => void;
    setTipoBeneficiario: (v: "cliente" | "fornecedor") => void;
  }
) {
  setters.setBeneficiario("");
  setters.setValor("");
  setters.setDataVencimento("");
  setters.setFormaPagamento("");
  setters.setClienteId("");
  setters.setFornecedorId("");
  setters.setObservacoes("");
  setters.setTipoBeneficiario("cliente");
}

export function NovaDespesaDialog({ open, onOpenChange, onSuccess, dadosIniciais }: NovaDespesaDialogProps) {
  const criarTransacao = useCriarTransacaoFinanceira();
  const { data: clientes = [] } = useClientes(500);
  const { data: fornecedores = [] } = useFornecedores(true);
  const { data: formasPagamento = [] } = useFormasPagamentoAtivas();

  const [beneficiario, setBeneficiario] = useState("");
  const [valor, setValor] = useState("");
  const [dataVencimento, setDataVencimento] = useState("");
  const [formaPagamento, setFormaPagamento] = useState("");
  const [tipoBeneficiario, setTipoBeneficiario] = useState<"cliente" | "fornecedor">("cliente");
  const [clienteId, setClienteId] = useState("");
  const [fornecedorId, setFornecedorId] = useState("");
  const [observacoes, setObservacoes] = useState("");

  useEffect(() => {
    if (!open) return;
    if (dadosIniciais) {
      setTipoBeneficiario("cliente");
      setFornecedorId("");
      if (dadosIniciais.clienteId) setClienteId(String(dadosIniciais.clienteId));
      if (dadosIniciais.valor != null) {
        setValor(
          dadosIniciais.valor.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: false,
          })
        );
      }
      if (dadosIniciais.dataVencimento) setDataVencimento(dadosIniciais.dataVencimento);
      if (dadosIniciais.observacoes) setObservacoes(dadosIniciais.observacoes);
    } else {
      resetForm({
        setBeneficiario,
        setValor,
        setDataVencimento,
        setFormaPagamento,
        setClienteId,
        setFornecedorId,
        setObservacoes,
        setTipoBeneficiario,
      });
    }
  }, [open, dadosIniciais]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valorNum = parseFloat(valor.replace(",", "."));
    const idCliente = tipoBeneficiario === "cliente" ? clienteId : "";
    const idFornecedor = tipoBeneficiario === "fornecedor" ? fornecedorId : "";
    if ((!idCliente && !idFornecedor) || !valorNum || valorNum <= 0 || !dataVencimento || !formaPagamento) {
      toast.error("Selecione cliente ou fornecedor, preencha valor, vencimento e forma de pagamento.");
      return;
    }

    try {
      const observacoesCompletas = beneficiario.trim()
        ? (observacoes.trim() ? `Beneficiário: ${beneficiario.trim()}. ${observacoes.trim()}` : `Beneficiário: ${beneficiario.trim()}`)
        : observacoes.trim();

      const formaPagamentoNome =
        formasPagamento.find((fp: { id: string | number; nome: string }) => String(fp.id) === String(formaPagamento))?.nome ??
        String(formaPagamento);

      const resultado = await criarTransacao.mutateAsync({
        sinistro_id: dadosIniciais?.sinistroId,
        cliente_id: tipoBeneficiario === "cliente" && idCliente ? parseInt(idCliente, 10) : undefined,
        fornecedor_id: tipoBeneficiario === "fornecedor" && idFornecedor ? parseInt(idFornecedor, 10) : undefined,
        tipo: "saida",
        valor: valorNum,
        forma_pagamento: formaPagamentoNome,
        data_vencimento: dataVencimento,
        observacoes: observacoesCompletas || undefined,
      });

      if ((resultado as { _sinistroIdOmitido?: boolean })._sinistroIdOmitido) {
        toast.success("Despesa cadastrada. Execute a migration sinistro_id no Supabase para vincular ao sinistro.");
      } else {
        toast.success("Despesa cadastrada com sucesso.");
      }
      resetForm({
        setBeneficiario,
        setValor,
        setDataVencimento,
        setFormaPagamento,
        setClienteId,
        setFornecedorId,
        setObservacoes,
        setTipoBeneficiario,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Erro ao criar despesa:", err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("sinistro_id") && msg.includes("schema cache")) {
        toast.error(
          "Coluna sinistro_id ausente no banco. Execute a migration 20260519000002_add_sinistro_id_to_transacoes_financeiras no Supabase."
        );
      } else {
        toast.error(msg || "Erro ao cadastrar despesa. Tente novamente.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-red-600" />
            {dadosIniciais?.titulo ?? "Nova despesa (conta a pagar)"}
          </DialogTitle>
        </DialogHeader>
        {dadosIniciais?.sinistroId && (
          <p className="text-sm text-gray-600 -mt-2">
            Dados pré-preenchidos a partir do sinistro #{dadosIniciais.sinistroId}. Revise e confirme.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Beneficiário *</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={tipoBeneficiario === "cliente" ? "default" : "outline"}
                size="sm"
                onClick={() => { setTipoBeneficiario("cliente"); setFornecedorId(""); }}
                className="flex-1"
              >
                <User className="h-4 w-4 mr-1" />
                Cliente
              </Button>
              <Button
                type="button"
                variant={tipoBeneficiario === "fornecedor" ? "default" : "outline"}
                size="sm"
                onClick={() => { setTipoBeneficiario("fornecedor"); setClienteId(""); }}
                className="flex-1"
              >
                <Package className="h-4 w-4 mr-1" />
                Fornecedor
              </Button>
            </div>
          </div>

          {tipoBeneficiario === "cliente" ? (
            <div className="space-y-2">
              <Label htmlFor="cliente_id">Cliente / Conta *</Label>
              <Select value={clienteId} onValueChange={setClienteId} required={tipoBeneficiario === "cliente"}>
                <SelectTrigger id="cliente_id">
                  <SelectValue placeholder="Selecione o cliente ou Despesas gerais" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="fornecedor_id">Fornecedor *</Label>
              <Select value={fornecedorId} onValueChange={setFornecedorId} required={tipoBeneficiario === "fornecedor"}>
                <SelectTrigger id="fornecedor_id">
                  <SelectValue placeholder="Selecione o fornecedor" />
                </SelectTrigger>
                <SelectContent>
                  {fornecedores.map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>{f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="beneficiario">Beneficiário (texto livre, opcional)</Label>
            <Input
              id="beneficiario"
              value={beneficiario}
              onChange={(e) => setBeneficiario(e.target.value)}
              placeholder="Ex: Nome do fornecedor para referência"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="valor">Valor (R$) *</Label>
              <Input
                id="valor"
                type="text"
                inputMode="decimal"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0,00"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_vencimento">Vencimento *</Label>
              <Input
                id="data_vencimento"
                type="date"
                value={dataVencimento}
                onChange={(e) => setDataVencimento(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="forma_pagamento">Forma de pagamento *</Label>
            <Select value={formaPagamento} onValueChange={setFormaPagamento} required>
              <SelectTrigger id="forma_pagamento">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {formasPagamento.map((fp: { id: string | number; nome: string }) => (
                  <SelectItem key={String(fp.id)} value={String(fp.id)}>
                    {fp.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Detalhes da despesa..."
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={criarTransacao.isPending}>
              {criarTransacao.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Cadastrar despesa
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
